import tw, { styled } from 'twin.macro';
import ReactCrop, { Crop, PercentCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import React, { useEffect, useRef, useState } from 'react';

import { Error } from 'components/typography/Typography';
import {
  centeredCropForAspect,
  fitPxCropToAspect,
  percentToPx,
  pxToPercent,
} from './cropGeometry';
import {
  CropMinimum,
  cropClearsMinimum,
} from 'components/video-gen/btlRefusal';

interface AdEditorImageCropperProps {
  uuid: string;
  image: string;
  crop: Crop | null;
  aspect?: number;
  /** Fires on every pixel change during drag — used for live canvas preview. */
  onCrop?: (crop: Crop | null) => void;
  /** Fires once when the user finishes dragging — used for state persistence. */
  onCropComplete?: (crop: Crop | null) => void;
  /**
   * When true, on mount the initial rectangle is normalised to match `aspect`
   * (centered default when no crop, or re-fitted when the stored crop's aspect
   * diverges) and `onCropComplete` fires once so parent state stays in sync.
   * Off by default — ad-builder relies on the stored crop being preserved
   * verbatim until the user drags, because `onCropComplete` persists to canvas.
   */
  autoFitAspectOnMount?: boolean;
  /**
   * SYM-6096. When set, the box is drawn green while the crop covers at least
   * this much of the picture and red while it does not. The numbers come from
   * the server's refusal; the box keeps the placement's shape while dragged,
   * so one colour answers for both axes. Unset: an ordinary crop box.
   */
  minimumCoverage?: CropMinimum;
}

const AdEditorImageCropper: React.FC<AdEditorImageCropperProps> = ({
  uuid,
  image,
  crop,
  onCrop,
  onCropComplete,
  aspect = 1,
  autoFitAspectOnMount = false,
  minimumCoverage,
}) => {
  const imgRef = useRef<HTMLImageElement | null>(null);

  const [tempCrop, setTempCrop] = useState<Crop>({
    unit: 'px',
    x: 0,
    y: 0,
    width: 100,
    height: 100,
  });

  const [hasErrors] = useState(false);
  // The crop as a share of the picture, kept so the verdict can follow a drag.
  const [percentOfPicture, setPercentOfPicture] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const verdict: Verdict = !minimumCoverage
    ? undefined
    : percentOfPicture && cropClearsMinimum(percentOfPicture, minimumCoverage)
    ? 'ok'
    : 'short';

  const syncCropToTemp = (cropValue: Crop | null) => {
    const img = imgRef.current;
    if (!img) return;
    if (cropValue?.unit === '%' && cropValue.width && cropValue.height) {
      setPercentOfPicture({ width: cropValue.width, height: cropValue.height });
    }

    // ReactCrop works in DISPLAYED (rendered) coordinates, so we must use
    // img.width / img.height (the size in the DOM), NOT naturalWidth/naturalHeight.
    //
    // SYM-6198. Both numbers come from the SAME measurement, and nothing is
    // done until there is one. Taken separately, a picture mid-layout can give
    // a rendered width of 0 and a rendered height that is already set, and the
    // old `img.width || img.naturalWidth` then paired a natural width with a
    // rendered height. That is not a difference of scale, which the percentages
    // survive, but a difference of SHAPE, which moves the rectangle. The
    // arithmetic itself was cleared by test: see cropGeometry.test.ts, where a
    // rectangle is stored and read back five times over without moving.
    const rendered = img.width > 0 && img.height > 0;
    const w = rendered ? img.width : img.naturalWidth;
    const h = rendered ? img.height : img.naturalHeight;
    if (!w || !h) return;

    if (!cropValue || cropValue.width == null || cropValue.height == null) {
      if (autoFitAspectOnMount) {
        // Default rectangle honours `aspect` so it already matches the final
        // output, rather than the full image (which would display as free-form
        // until the user drags).
        const def = centeredCropForAspect(w, h, aspect);
        setTempCrop(def);
        onCropComplete?.(pxToPercent(def, w, h));
      } else {
        setTempCrop({ unit: 'px', x: 0, y: 0, width: w, height: h });
      }
      return;
    }

    if (cropValue.unit === '%') {
      const px = percentToPx(
        {
          x: cropValue.x,
          y: cropValue.y,
          width: cropValue.width,
          height: cropValue.height,
        },
        w,
        h
      );
      if (autoFitAspectOnMount) {
        // If a stored crop was saved at a different aspect (e.g. an earlier 1:1
        // setting), adjust the initial rectangle to match the current aspect so
        // what's displayed lines up with the cropped video frame.
        const fitted = fitPxCropToAspect(px, aspect, w, h);
        setTempCrop(fitted);
        if (fitted.width !== px.width || fitted.height !== px.height) {
          onCropComplete?.(pxToPercent(fitted, w, h));
        }
      } else {
        setTempCrop({ unit: 'px', ...px });
      }
    }
  };

  useEffect(() => {
    syncCropToTemp(crop);
  }, [uuid, crop]);

  return (
    <CropperWrapper $verdict={verdict}>
      <ReactCrop
        crop={tempCrop}
        aspect={aspect}
        ruleOfThirds
        onChange={(c: Crop, percentCrop: PercentCrop) => {
          if (!c.width || !c.height) return;

          setTempCrop(c);
          setPercentOfPicture({
            width: percentCrop.width ?? 100,
            height: percentCrop.height ?? 100,
          });
          onCrop?.({
            unit: '%',
            x: percentCrop.x ?? 0,
            y: percentCrop.y ?? 0,
            width: percentCrop.width ?? 100,
            height: percentCrop.height ?? 100,
          });
        }}
        onComplete={(c: Crop, percentCrop: PercentCrop) => {
          if (!c.width || !c.height) return;

          setTempCrop(c);
          onCropComplete?.({
            unit: '%',
            x: percentCrop.x ?? 0,
            y: percentCrop.y ?? 0,
            width: percentCrop.width ?? 100,
            height: percentCrop.height ?? 100,
          });
        }}
      >
        <img
          ref={imgRef}
          src={image}
          alt=""
          crossOrigin="anonymous"
          onLoad={(e) => {
            imgRef.current = e.currentTarget;
            syncCropToTemp(crop);
          }}
        />
      </ReactCrop>

      {hasErrors && (
        <div tw="mt-3">
          <Error>
            Something went wrong while uploading your cropped image, please try
            again.
          </Error>
        </div>
      )}
    </CropperWrapper>
  );
};

type Verdict = 'ok' | 'short' | undefined;

// The library draws the box as a dashed white line; while a minimum applies the
// line and the handles take the verdict's colour instead (SYM-6096).
const verdictColour = (verdict: Verdict) =>
  verdict === 'ok' ? '#22c55e' : verdict === 'short' ? '#ef4444' : undefined;

const CropperWrapper = styled.div<{ $verdict?: Verdict }>`
  ${tw`flex flex-col object-contain w-fit h-auto max-w-[90vw] max-h-[80vw] lg:(max-w-[400px] max-h-[none])`}
  ${({ $verdict }) =>
    verdictColour($verdict) &&
    `
    .ReactCrop__crop-selection {
      border: 2px solid ${verdictColour($verdict)};
    }
    .ReactCrop__drag-handle::after {
      background-color: ${verdictColour($verdict)};
      border-color: ${verdictColour($verdict)};
    }
  `}
`;

export default AdEditorImageCropper;
